// BUILD-046 Owner-requested visual edit (after candidate fb6aee68): the quick date buttons under a date field - Today, Tomorrow, Next week, Clear.
//
// Pure date arithmetic on a CALENDAR DATE written `YYYY-MM-DD`. A date-only string is never parsed with `new Date(string)` and no local time zone is
// involved: the year, month and day are split out, the day count is added with `Date.UTC` (which has no daylight saving), and the result is written back
// from the UTC parts. "Today" is the company's calendar day in Phoenix (`phoenixToday` of dateLabels, read at the moment of the click by the component).
//
//   today       the Phoenix date
//   tomorrow    today + 1 day
//   next-week   today + 7 days
//   clear       '' (the field is emptied; an empty Task due date is sent as no due date, exactly as before)
//
// The component (QuickDateField.tsx) is generic: it takes a label, so the same buttons can serve another date field later.
//
// Pure module apart from the dateLabels import (itself pure apart from an Intl formatter). Erasable TypeScript only.

import { isIsoDate } from './dateLabels';

export type QuickDateChoice = 'today' | 'tomorrow' | 'next-week' | 'clear';

export interface QuickDateButtonSpec {
  choice: QuickDateChoice;
  /** The short visible text of the button. */
  text: string;
  /** The end of the accessible name: "Set <field label> to <phrase>"; the clear button reads "Clear <field label>". */
  phrase: string;
  /** Days added to today; null for the clear button. */
  days: number | null;
}

/** The buttons in the order they are shown. */
export const QUICK_DATE_BUTTONS: readonly QuickDateButtonSpec[] = Object.freeze([
  { choice: 'today' as QuickDateChoice, text: 'Today', phrase: 'today', days: 0 },
  { choice: 'tomorrow' as QuickDateChoice, text: 'Tomorrow', phrase: 'tomorrow', days: 1 },
  { choice: 'next-week' as QuickDateChoice, text: 'Next week', phrase: 'next week', days: 7 },
  { choice: 'clear' as QuickDateChoice, text: 'Clear', phrase: 'clear', days: null },
]);

/**
 * `iso` plus `days` calendar days (negative goes back), as `YYYY-MM-DD`. Null when `iso` is not a real calendar date, `days` is not a whole number, or the
 * result leaves the years 0100 - 9999 that the date input and the server's date check use.
 */
export function addDaysToIsoDate(iso: unknown, days: unknown): string | null {
  if (!isIsoDate(iso) || typeof days !== 'number' || !Number.isInteger(days)) return null;
  const [year, month, day] = iso.split('-').map(Number);
  const moved = new Date(Date.UTC(year, month - 1, day + days));
  const y = moved.getUTCFullYear();
  if (y < 100 || y > 9999) return null;
  const text = `${String(y).padStart(4, '0')}-${String(moved.getUTCMonth() + 1).padStart(2, '0')}-${String(moved.getUTCDate()).padStart(2, '0')}`;
  return isIsoDate(text) ? text : null;
}

/**
 * The value a quick button puts in the field: '' for Clear, today / tomorrow / next week as `YYYY-MM-DD` otherwise. Null when `today` is not a real date
 * (nothing should change then).
 */
export function quickDateValue(choice: QuickDateChoice, today: string): string | null {
  const spec = QUICK_DATE_BUTTONS.find((b) => b.choice === choice);
  if (spec === undefined) return null;
  if (spec.days === null) return '';
  return addDaysToIsoDate(today, spec.days);
}

export interface QuickDateButtonView {
  choice: QuickDateChoice;
  text: string;
  /** The accessible name, for example "Set Task due date to tomorrow" or "Clear Task due date". */
  name: string;
}

/** The buttons for the field called `label`, with their accessible names. */
export function describeQuickDateButtons(label: string): QuickDateButtonView[] {
  const field = typeof label === 'string' && label.trim() !== '' ? label.trim() : 'date';
  return QUICK_DATE_BUTTONS.map((b) => ({ choice: b.choice, text: b.text, name: b.days === null ? `Clear ${field}` : `Set ${field} to ${b.phrase}` }));
}
